import DOMPurify from 'dompurify';

/**
 * VRG-104 — brand-authored HTML, made safe to hand to `dangerouslySetInnerHTML` on a
 * certificate page.
 *
 * ## Why this had to exist before custom fields could render
 *
 * Two surfaces on this page take HTML a BRAND wrote and set it as `innerHTML`:
 * the product description (which has done so, unsanitised, since long before this
 * ticket) and now a `rich` custom field. The certificate app is ONE origin shared by
 * every brand and it holds the owner's session, so markup that executes here is not
 * scoped to the tenant that authored it — a compromised brand admin account, or a
 * compromised connected store whose description we import verbatim, reaches every
 * reader of every certificate on the origin.
 *
 * ## Sanitise on RENDER, not only on write
 *
 * The same argument `apps/platform`'s `announcementBody.ts` sets out, and it applies
 * with more force here: product descriptions are IMPORTED. Rows were written by code
 * paths that never validated anything, by integrations, by seeds, and by direct SQL.
 * A write-side filter trusts data it has never seen; render-side sanitising is
 * idempotent, applies to every row whatever wrote it, and sits on the same side of the
 * wire as the `innerHTML` that makes it necessary.
 *
 * The API's `normalizeProductCustomFields` is still the authoring gate for a `link`
 * field's URL — this file is the second half of that guarantee, which is that `rich`
 * HTML cannot carry an `<a>` and become an unvalidated second way to author one.
 *
 * ## Two allow-lists, deliberately not one
 *
 * They answer different questions and sharing one would make both wrong:
 *
 *   - A CUSTOM FIELD is new, so there is no legacy content to accommodate and the
 *     `link` type is the sanctioned way to point somewhere. `<a>` and every image are
 *     out.
 *   - A DESCRIPTION is existing product copy, often imported from a store. It
 *     legitimately contains links and images today, and stripping them would silently
 *     delete brand content that has been on the page for a year. So it keeps them, with
 *     the schemes restricted and `rel="noopener noreferrer"` forced.
 *
 * ⚠️ That asymmetry is honest but it is not fully coherent: a brand that wants an
 * unvalidated link on a certificate can still write one in the DESCRIPTION, so "a link
 * on a certificate is https and shows its host" is true of the `link` field type and not
 * of the page. Tightening the description to the same rule would change what existing
 * brand copy is allowed to say, which is a product decision rather than a refactor.
 * Raised rather than quietly decided.
 */

/**
 * Tags a `rich` custom field may contain. Everything else is stripped (its TEXT is
 * kept — DOMPurify's default — so a brand never loses words, only markup).
 *
 * An ALLOW-list, never a deny-list: dangerous markup is an open-ended set that grows
 * with every browser feature, while what a block of brand copy legitimately needs is
 * short and closed. This is what the platform's editor toolbar can actually produce —
 * headings, bold/italic, inline and block code, lists, blockquote.
 *
 * NOTABLY ABSENT, each for a reason:
 *
 *   - `a` — the `link` custom-field TYPE is the authoring path for a link, and it is
 *     validated (https-only) and renders its destination host beside itself. An `<a>`
 *     smuggled through rich text would have neither, which would make the validation
 *     decorative rather than a rule.
 *   - `script`, `style` — arbitrary execution, and (for `style`) selectors that reach
 *     out of the block and restyle the certificate around it.
 *   - `iframe`, `object`, `embed` — a third-party origin inside the certificate.
 *   - `form`, `input`, `button` — a credential prompt rendered inside a page whose
 *     whole claim is that it can be trusted is the most convincing phishing surface
 *     there is.
 *   - `img`, `svg`, `video` — not execution but a REQUEST: a remote `src` fires on
 *     render and hands every scanner's IP, user-agent and read-time to whoever controls
 *     the host. The product's own images are a first-class field; a custom field has
 *     never needed one, so allowing it buys a tracking pixel and nothing else.
 */
const CUSTOM_FIELD_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'blockquote',
  'code',
  'pre',
];

/**
 * A description keeps links and images — see the two-allow-lists note above. `figure`
 * and `figcaption` are in because imported store descriptions use them around images.
 */
const DESCRIPTION_TAGS = [
  ...CUSTOM_FIELD_TAGS,
  'a',
  'img',
  'figure',
  'figcaption',
  'hr',
  'span',
  'div',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
];

/**
 * Attributes that survive on a custom field: ONE, and it is ours.
 *
 * The event handlers are the point of an attribute allow-list. `<b onmouseover="…">`
 * needs no script tag and no network request; it is inline execution hanging off a tag
 * that IS allowed, which is why a tag allow-list alone is not a mitigation. DOMPurify
 * drops every attribute not named here, so `on*`, `style`, `formaction` and any
 * `data-*` go without being enumerated.
 *
 * `data-align` is written by `carryTextAlign` below, never by the author.
 */
const CUSTOM_FIELD_ATTR = ['data-align'];

const DESCRIPTION_ATTR = [...CUSTOM_FIELD_ATTR, 'href', 'target', 'rel', 'src', 'alt', 'title'];

/**
 * Schemes a description's link or image may use.
 *
 * `javascript:` is the one that matters — `<a href="javascript:…">` is execution on
 * click through an otherwise entirely legitimate anchor, which is why an `href`
 * allow-list without a SCHEME allow-list is not enough. `data:` is excluded for the
 * same family of reason (`data:text/html` navigates to attacker-authored markup on this
 * origin's coat-tails).
 */
const DESCRIPTION_URI_REGEXP = /^(?:https?:|mailto:)/i;

/** Named explicitly so a future widening of a tag list cannot readmit them by accident. */
const FORBID_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'];

let hooksRegistered = false;

/**
 * Registered once at module load, not per call: DOMPurify hooks are GLOBAL, so adding
 * one inside a sanitize function stacks a duplicate on every render — a slow leak that
 * only shows as the page getting heavier the longer it is open.
 */
const registerHooks = () => {
  if (hooksRegistered) return;
  hooksRegistered = true;

  /*
   * ALIGNMENT, carried across the `style` ban.
   *
   * The platform's rich-text toolbar offers text-align, and tiptap writes it as
   * `style="text-align: center"` — an attribute this allow-list drops. Without this
   * hook a brand centres a paragraph, saves, and the certificate silently ignores it:
   * a control that reports success and changes nothing, which is worse than not
   * offering alignment at all.
   *
   * `beforeSanitizeAttributes` is the only hook that can do this. It runs while `style`
   * is still on the node; by `afterSanitizeAttributes` the attribute is already gone.
   * The value is read through the parsed `style` object and then written back as our own
   * `data-align`, so nothing from the author's CSS survives — only one of three literal
   * keywords we recognise.
   */
  DOMPurify.addHook('beforeSanitizeAttributes', node => {
    const element = node as unknown as HTMLElement;
    if (!element.style) return;
    const align = element.style.textAlign;
    if (align === 'center' || align === 'right' || align === 'justify') {
      element.setAttribute('data-align', align);
    }
  });

  /*
   * Every external link in a DESCRIPTION leaves with `rel="noopener noreferrer"`.
   *
   * `noopener` is a security fix, not a nicety: without it the opened page gets a live
   * `window.opener` handle to the certificate and can navigate it anywhere — reverse
   * tabnabbing, where the tab the reader returns to is no longer the certificate they
   * left. On a page whose purpose is proving authenticity that is the whole attack.
   * `noreferrer` keeps the certificate's URL (which identifies the item) out of the
   * destination's referrer log.
   *
   * Harmless for custom fields, which have no `<a>` to find.
   */
  DOMPurify.addHook('afterSanitizeAttributes', node => {
    const element = node as unknown as HTMLElement;
    if (element.tagName !== 'A') return;
    element.setAttribute('target', '_blank');
    element.setAttribute('rel', 'noopener noreferrer');
  });
};

const sanitize = (
  html: string,
  allowedTags: string[],
  allowedAttr: string[],
  uriRegexp?: RegExp,
): string => {
  registerHooks();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttr,
    ...(uriRegexp ? {ALLOWED_URI_REGEXP: uriRegexp} : null),
    FORBID_TAGS,
    // Return a string, not a fragment — this feeds `dangerouslySetInnerHTML`.
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  }) as unknown as string;
};

/**
 * A `rich` custom field's HTML, safe to render.
 *
 * Call this at EVERY render site. It is cheap and idempotent, and the moment one surface
 * renders the raw string instead, the guarantee is gone for that surface alone — the
 * hardest kind of hole to notice, because the others still look fine.
 *
 * Accepts null/undefined and returns '' for them, rather than making each caller guard.
 * A nullable column is the normal case here — `custom_fields[].value` and
 * `product.experience_content` are both optional — and a signature that forces `html!`
 * or `?? ''` at the call site is a signature that occasionally gets answered with a
 * raw render instead, which is precisely the hole the paragraph above is about.
 */
export const sanitizeCustomFieldHtml = (html: string | null | undefined): string =>
  html ? sanitize(html, CUSTOM_FIELD_TAGS, CUSTOM_FIELD_ATTR) : '';

/** A product description's HTML, safe to render. Keeps links and images; see above. */
export const sanitizeProductDescription = (html: string): string =>
  sanitize(html, DESCRIPTION_TAGS, DESCRIPTION_ATTR, DESCRIPTION_URI_REGEXP);

/**
 * Does this HTML contain any actual TEXT?
 *
 * `html.trim()` is the obvious check and is wrong for rich text: an empty tiptap
 * document serialises to `<p></p>`, four characters of non-whitespace — so a block with
 * nothing in it would render its title and an empty space below it. This strips the
 * markup and asks the real question.
 */
export const customFieldHtmlHasText = (html: string | null | undefined): boolean => {
  if (!html) return false;
  const text = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  }) as unknown as string;
  return text.replace(/\s|&nbsp;/g, '').length > 0;
};
